import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/frontend-plugin-api';
import { BackendAuthApi } from '@internal/plugin-neo4j-auth-common';
import {
  AuditApi,
  AuditEvent,
  AuditEventEntityKindEnum,
  AuditEventList,
  ClusterDiscoveryCluster,
  ClusterDiscoveryPartition,
  ClusterDiscoveryTopologyToJSON as clusterDiscoveryTopologyToJSON,
  ClusterEntity,
  // Serialisers aliased to lowercase: eslint new-cap would otherwise treat
  // the generated PascalCase function names as constructor calls.
  ClusterEntityToJSON as clusterEntityToJSON,
  ClustersApi,
  ClusterSpecInput,
  Condition,
  ConditionRule,
  ConditionsApi,
  Configuration,
  EnvironmentsApi,
  IsolationUnitEntity,
  IsolationUnitEntityToJSON as isolationUnitEntityToJSON,
  IsolationUnitsApi,
  IsolationUnitSpecInput,
  Metadata,
  PartitionEntity,
  PartitionEntityToJSON as partitionEntityToJSON,
  PartitionsApi,
  PartitionSpecInput,
  ResponseError,
} from '@internal/backstage-plugin-neo4j-ims-control-panel-common';

import { ImsDeployment, IMS_DEPLOYMENTS, IMS_PROXY_PATHS } from './const';

/**
 * Client for ims-management-api, built on the SDK generated from the
 * component's committed OpenAPI spec (components/ims-management-api/docs/
 * openapi3.yaml) into the neo4j-ims-control-panel-common package. This file
 * only adapts the per-kind SDK classes to the kind-parameterised interface
 * the UI works with, and wires Backstage discovery/fetch/auth into them.
 */

export type ImsKind = 'partitions' | 'isolation-units' | 'clusters';

export type ImsCondition = Condition;

/**
 * A condition IMS derives (ADR-0028): `type` is True when every condition in
 * `allOf` is True, and False otherwise.
 */
export type ImsConditionRule = { type: string; allOf: string[] };

/** The derived condition rules of each kind, in IMS's evaluation order. */
export type ImsConditionRules = Record<ImsKind, ImsConditionRule[]>;

/**
 * The generated spec marks every property optional (swag emits no `required`
 * markers), but the API always returns the envelope fields (ADR-0008); narrow
 * them so the UI does not need identity null-guards everywhere.
 */
type ImsEnvelope = {
  id: string;
  environment: string;
  managementSource: string;
  createdAt: string;
  updatedAt: string;
  metadata: Metadata;
};

export type ImsPartition = PartitionEntity & ImsEnvelope;
export type ImsIsolationUnit = IsolationUnitEntity & ImsEnvelope;
export type ImsCluster = ClusterEntity & ImsEnvelope;

export type ImsEntity = ImsPartition | ImsIsolationUnit | ImsCluster;

export type ImsSpecInput =
  | PartitionSpecInput
  | IsolationUnitSpecInput
  | ClusterSpecInput;

/**
 * The API always returns the identity of an audit event (the `audit_events`
 * columns are NOT NULL); narrowed for the same reason as ImsEnvelope.
 */
export type ImsAuditEvent = AuditEvent & {
  id: number;
  occurredAt: string;
  entityKind: AuditEventEntityKindEnum;
  entityId: string;
  operation: NonNullable<AuditEvent['operation']>;
};

export type ImsAuditEventPage = {
  items: ImsAuditEvent[];
  /** Absent on the last page. */
  nextPageToken?: string;
};

/** Server-side window and cursor for an audit listing, newest first. */
export type ImsAuditEventQuery = {
  /** RFC 3339; only events at or after this time. */
  since?: string;
  pageToken?: string;
  limit?: number;
};

export type ImsTopologyRelation = NonNullable<
  ClusterDiscoveryCluster['relation']
>;

export type ImsTopologyPartition = ClusterDiscoveryPartition & {
  name: string;
  relation: ImsTopologyRelation;
};

export type ImsTopologyCluster = ClusterDiscoveryCluster & {
  name: string;
  relation: ImsTopologyRelation;
  roles: string[];
};

/**
 * What cluster-discovery hands to one target cluster
 * (GET /v1/clusters/{id}/topology): the partitions and clusters it can see,
 * by operational name, each related to the target.
 */
export type ImsClusterTopology = {
  partitions: ImsTopologyPartition[];
  clusters: ImsTopologyCluster[];
};

export function imsClusterTopologyToWireJson(
  topology: ImsClusterTopology,
): object {
  return clusterDiscoveryTopologyToJSON(topology);
}

export const AUDIT_ENTITY_KINDS: Record<AuditEventEntityKindEnum, ImsKind> = {
  partition: 'partitions',
  isolation_unit: 'isolation-units',
  cluster: 'clusters',
};

/**
 * Wire-format (snake_case) JSON for an entity, as the API returned it. The
 * SDK deserialises responses into camelCase models; the raw-data view should
 * show what is actually on the wire.
 */
export function imsEntityToWireJson(kind: ImsKind, entity: ImsEntity): object {
  switch (kind) {
    case 'partitions':
      return partitionEntityToJSON(entity as ImsPartition);
    case 'isolation-units':
      return isolationUnitEntityToJSON(entity as ImsIsolationUnit);
    case 'clusters':
    default:
      return clusterEntityToJSON(entity as ImsCluster);
  }
}

/**
 * Every call targets one IMS deployment (each has its own API behind its own
 * proxy entry, see IMS_PROXY_PATHS). Reads and writes of catalog entities are
 * additionally scoped to one catalog environment within that deployment —
 * the API has no cross-environment list.
 */
export interface ImsControlPanelApi {
  /** Catalog environments managed by a deployment (GET /v1/environments). */
  listEnvironments(deployment: ImsDeployment): Promise<string[]>;
  listEntities(
    deployment: ImsDeployment,
    environment: string,
    kind: ImsKind,
  ): Promise<ImsEntity[]>;
  getEntity(
    deployment: ImsDeployment,
    kind: ImsKind,
    id: string,
  ): Promise<ImsEntity>;
  /** POST to the environment collection; IMS assigns id and name (ADR-0005). */
  createEntity(
    deployment: ImsDeployment,
    environment: string,
    kind: ImsKind,
    spec: ImsSpecInput,
  ): Promise<ImsEntity>;
  /** PUT replaces the full spec. */
  updateEntitySpec(
    deployment: ImsDeployment,
    kind: ImsKind,
    id: string,
    spec: ImsSpecInput,
  ): Promise<ImsEntity>;
  deleteEntity(
    deployment: ImsDeployment,
    kind: ImsKind,
    id: string,
  ): Promise<void>;
  /** Changes to every entity of one catalog environment. */
  listAuditEvents(
    deployment: ImsDeployment,
    environment: string,
    query: ImsAuditEventQuery,
  ): Promise<ImsAuditEventPage>;
  /** Changes to one entity; still answers after the entity is deleted. */
  listEntityAuditEvents(
    deployment: ImsDeployment,
    kind: ImsKind,
    id: string,
    query: ImsAuditEventQuery,
  ): Promise<ImsAuditEventPage>;
  getAuditEvent(deployment: ImsDeployment, id: number): Promise<ImsAuditEvent>;
  /** The scoped topology view cluster-discovery reads for one cluster. */
  getClusterTopology(
    deployment: ImsDeployment,
    id: string,
  ): Promise<ImsClusterTopology>;
  /** The conditions the deployment derives from others, per kind. */
  getConditionRules(deployment: ImsDeployment): Promise<ImsConditionRules>;
}

export const imsControlPanelApiRef: ApiRef<ImsControlPanelApi> =
  createApiRef<ImsControlPanelApi>({
    id: 'plugin.neo4j-ims-control-panel.service',
  });

type KindClients = {
  audit: AuditApi;
  conditions: ConditionsApi;
  environments: EnvironmentsApi;
  partitions: PartitionsApi;
  isolationUnits: IsolationUnitsApi;
  clusters: ClustersApi;
};

export class ImsControlPanelRestApi implements ImsControlPanelApi {
  private readonly clients: Record<ImsDeployment, KindClients>;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    backendAuthApi: BackendAuthApi;
  }) {
    const { discoveryApi, fetchApi, backendAuthApi } = options;

    const clientsFor = (deployment: ImsDeployment): KindClients => {
      const configuration = new Configuration({
        // The origin is only known per request: the SDK's path is appended
        // to the Backstage proxy base (resolved through discovery) plus this
        // deployment's proxy entry. The user's Okta token travels in a
        // dedicated header, validated by ims-management-api itself; the
        // proxy entry uses `credentials: require`, so Authorization is
        // reserved for the Backstage identity token that fetchApi attaches.
        basePath: '',
        fetchApi: async (input, init) => {
          const baseUrl = await discoveryApi.getBaseUrl('proxy');
          const headers = new Headers(init?.headers);
          const token = await backendAuthApi.getToken();
          if (token) {
            headers.set('Aura-Portal-Okta-Token', token);
          }
          return fetchApi.fetch(
            `${baseUrl}${IMS_PROXY_PATHS[deployment]}${String(input)}`,
            { ...init, headers },
          );
        },
      });
      return {
        audit: new AuditApi(configuration),
        conditions: new ConditionsApi(configuration),
        environments: new EnvironmentsApi(configuration),
        partitions: new PartitionsApi(configuration),
        isolationUnits: new IsolationUnitsApi(configuration),
        clusters: new ClustersApi(configuration),
      };
    };

    this.clients = Object.fromEntries(
      IMS_DEPLOYMENTS.map(deployment => [deployment, clientsFor(deployment)]),
    ) as Record<ImsDeployment, KindClients>;
  }

  async listEnvironments(deployment: ImsDeployment): Promise<string[]> {
    const body = await this.unwrap(
      this.clients[deployment].environments.v1EnvironmentsGet(),
    );
    return (body.items ?? []).filter(Boolean);
  }

  /** Rethrows SDK errors with the API's own error message when available. */
  private async unwrap<T>(promise: Promise<T>): Promise<T> {
    try {
      return await promise;
    } catch (error) {
      if (error instanceof ResponseError) {
        let message = `${error.response.status} ${error.response.statusText}`;
        try {
          const body = await error.response.json();
          if (body?.message) {
            message = body.message;
          }
        } catch {
          // keep the status line
        }
        throw new Error(`ims-management-api request failed: ${message}`);
      }
      throw error;
    }
  }

  async listEntities(
    deployment: ImsDeployment,
    environment: string,
    kind: ImsKind,
  ): Promise<ImsEntity[]> {
    const clients = this.clients[deployment];
    switch (kind) {
      case 'partitions': {
        const list = await this.unwrap(
          clients.partitions.v1EnvironmentsEnvironmentPartitionsGet({
            environment,
          }),
        );
        return (list.items ?? []) as ImsPartition[];
      }
      case 'isolation-units': {
        const list = await this.unwrap(
          clients.isolationUnits.v1EnvironmentsEnvironmentIsolationUnitsGet({
            environment,
          }),
        );
        return (list.items ?? []) as ImsIsolationUnit[];
      }
      case 'clusters':
      default: {
        const list = await this.unwrap(
          clients.clusters.v1EnvironmentsEnvironmentClustersGet({
            environment,
          }),
        );
        return (list.items ?? []) as ImsCluster[];
      }
    }
  }

  async getEntity(
    deployment: ImsDeployment,
    kind: ImsKind,
    id: string,
  ): Promise<ImsEntity> {
    const clients = this.clients[deployment];
    switch (kind) {
      case 'partitions':
        return (await this.unwrap(
          clients.partitions.v1PartitionsIdGet({ id }),
        )) as ImsPartition;
      case 'isolation-units':
        return (await this.unwrap(
          clients.isolationUnits.v1IsolationUnitsIdGet({ id }),
        )) as ImsIsolationUnit;
      case 'clusters':
      default:
        return (await this.unwrap(
          clients.clusters.v1ClustersIdGet({ id }),
        )) as ImsCluster;
    }
  }

  async createEntity(
    deployment: ImsDeployment,
    environment: string,
    kind: ImsKind,
    spec: ImsSpecInput,
  ): Promise<ImsEntity> {
    const clients = this.clients[deployment];
    switch (kind) {
      case 'partitions':
        return (await this.unwrap(
          clients.partitions.v1EnvironmentsEnvironmentPartitionsPost({
            environment,
            createPartition: { spec: spec as PartitionSpecInput },
          }),
        )) as ImsPartition;
      case 'isolation-units':
        return (await this.unwrap(
          clients.isolationUnits.v1EnvironmentsEnvironmentIsolationUnitsPost({
            environment,
            createIsolationUnit: { spec: spec as IsolationUnitSpecInput },
          }),
        )) as ImsIsolationUnit;
      case 'clusters':
      default:
        return (await this.unwrap(
          clients.clusters.v1EnvironmentsEnvironmentClustersPost({
            environment,
            createCluster: { spec: spec as ClusterSpecInput },
          }),
        )) as ImsCluster;
    }
  }

  async updateEntitySpec(
    deployment: ImsDeployment,
    kind: ImsKind,
    id: string,
    spec: ImsSpecInput,
  ): Promise<ImsEntity> {
    const clients = this.clients[deployment];
    switch (kind) {
      case 'partitions':
        return (await this.unwrap(
          clients.partitions.v1PartitionsIdPut({
            id,
            updatePartitionSpec: { spec: spec as PartitionSpecInput },
          }),
        )) as ImsPartition;
      case 'isolation-units':
        return (await this.unwrap(
          clients.isolationUnits.v1IsolationUnitsIdPut({
            id,
            updateIsolationUnitSpec: { spec: spec as IsolationUnitSpecInput },
          }),
        )) as ImsIsolationUnit;
      case 'clusters':
      default:
        return (await this.unwrap(
          clients.clusters.v1ClustersIdPut({
            id,
            updateClusterSpec: { spec: spec as ClusterSpecInput },
          }),
        )) as ImsCluster;
    }
  }

  async deleteEntity(
    deployment: ImsDeployment,
    kind: ImsKind,
    id: string,
  ): Promise<void> {
    const clients = this.clients[deployment];
    switch (kind) {
      case 'partitions':
        await this.unwrap(clients.partitions.v1PartitionsIdDelete({ id }));
        return;
      case 'isolation-units':
        await this.unwrap(
          clients.isolationUnits.v1IsolationUnitsIdDelete({ id }),
        );
        return;
      case 'clusters':
      default:
        await this.unwrap(clients.clusters.v1ClustersIdDelete({ id }));
    }
  }

  async listAuditEvents(
    deployment: ImsDeployment,
    environment: string,
    query: ImsAuditEventQuery,
  ): Promise<ImsAuditEventPage> {
    return toAuditEventPage(
      await this.unwrap(
        this.clients[deployment].audit.v1EnvironmentsEnvironmentAuditEventsGet({
          environment,
          ...query,
        }),
      ),
    );
  }

  async getAuditEvent(
    deployment: ImsDeployment,
    id: number,
  ): Promise<ImsAuditEvent> {
    return (await this.unwrap(
      this.clients[deployment].audit.v1AuditEventsIdGet({ id }),
    )) as ImsAuditEvent;
  }

  async getClusterTopology(
    deployment: ImsDeployment,
    id: string,
  ): Promise<ImsClusterTopology> {
    const topology = await this.unwrap(
      this.clients[deployment].clusters.v1ClustersIdTopologyGet({ id }),
    );
    return {
      partitions: (topology.partitions ?? []) as ImsTopologyPartition[],
      clusters: (topology.clusters ?? []).map(cluster => ({
        ...cluster,
        roles: cluster.roles ?? [],
      })) as ImsTopologyCluster[],
    };
  }

  async getConditionRules(
    deployment: ImsDeployment,
  ): Promise<ImsConditionRules> {
    const rules = await this.unwrap(
      this.clients[deployment].conditions.v1ConditionRulesGet(),
    );
    return {
      partitions: toConditionRules(rules.partitions),
      'isolation-units': toConditionRules(rules.isolationUnits),
      clusters: toConditionRules(rules.clusters),
    };
  }

  async listEntityAuditEvents(
    deployment: ImsDeployment,
    kind: ImsKind,
    id: string,
    query: ImsAuditEventQuery,
  ): Promise<ImsAuditEventPage> {
    const { audit } = this.clients[deployment];
    const request = { id, ...query };
    switch (kind) {
      case 'partitions':
        return toAuditEventPage(
          await this.unwrap(audit.v1PartitionsIdAuditEventsGet(request)),
        );
      case 'isolation-units':
        return toAuditEventPage(
          await this.unwrap(audit.v1IsolationUnitsIdAuditEventsGet(request)),
        );
      case 'clusters':
      default:
        return toAuditEventPage(
          await this.unwrap(audit.v1ClustersIdAuditEventsGet(request)),
        );
    }
  }
}

function toConditionRules(rules: ConditionRule[] = []): ImsConditionRule[] {
  return rules.flatMap(({ type, allOf }) =>
    type ? [{ type, allOf: allOf ?? [] }] : [],
  );
}

function toAuditEventPage(list: AuditEventList): ImsAuditEventPage {
  return {
    items: (list.items ?? []) as ImsAuditEvent[],
    nextPageToken: list.nextPageToken || undefined,
  };
}
